import { get } from "lodash";
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Segment, Grid, Header, Button, Divider } from "semantic-ui-react";
import { FieldArray } from "formik";

import * as Yup from "yup";
import * as Schemas from "../../forms/validationSchemas.js";

import ScheduleActionItem from "./ScheduleActionItem.jsx";

import { ROW_STATE } from "../../forms/formConstants.js";

import { selectCopy } from "../../../state/reducers/locale/copySelector.js";

class ScheduleActionsPanel extends PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    formikProps: PropTypes.object,
    readOnly: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    violationErrors: PropTypes.object,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    readOnly: false,
    disabled: false,
    copyKey: "schedule.actions"
  };

  constructor(props) {
    super(props);

    this.state = {
      timezone: get(props, "formikProps.values.timezone.value", "UTC"), // fallback to UTC time if no timezone is set
      newCount: get(props, "formikProps.values.actions.length", 0)
    };
  }

  static getDerivedStateFromProps(props, state) {
    const timezone = get(props, "formikProps.values.timezone.value", "UTC"); // fallback to UTC time if no timezone is set

    if (timezone !== state.timezone) {
      return {
        timezone
      };
    }

    return null;
  }

  // -- DATA VALIDATION -- //

  static validationScheme = function (messages) {
    return Yup.array().of(
      Yup.object().shape({
        value: Schemas.simpleStringSchema("Action Type", messages, true),
        at: Schemas.simpleStringSchema("Time", messages, true)
      })
    );
  };

  // -- NEW HANDLERS -- //

  addAction(push) {
    const { id } = this.props;
    const { newCount } = this.state;

    const actionId = `${id}[${newCount}]`;
    const action = {
      id: actionId,
      value: "",
      at: "",
      state: ROW_STATE.NEW
    };

    this.setState({ newCount: newCount + 1 });

    push(action);
  }

  editAction = id => {
    const { formikProps } = this.props;

    const currState = get(formikProps, `values.${id}.state`, null);

    // we only want to set the state to edit on previously existing actions
    if (currState !== ROW_STATE.NEW && currState !== ROW_STATE.DELETED) {
      formikProps.setFieldValue(`${id}.state`, ROW_STATE.EDIT);
    }
  };

  deleteAction = (id, index, remove) => {
    const { formikProps } = this.props;

    formikProps.setFieldValue(`${id}.state`, ROW_STATE.DELETE);

    // no need to retain actions that have never been saved
    if (get(formikProps, `values.${id}.state`, null) === ROW_STATE.NEW) {
      remove(index);
    }
  };

  // -- RENDER -- //

  renderActionItems = fieldProps => {
    const {
      id,
      formikProps,
      readOnly,
      disabled,
      violationErrors,
      copyKey,
      copy
    } = this.props;

    const { timezone } = this.state;

    const actions = get(formikProps, "values.actions", []);

    return actions.map((item, index) => {
      const actionId = `${id}[${index}]`;

      const itemMode = get(
        formikProps,
        `values.${actionId}.state`,
        ROW_STATE.VIEW
      );

      return (
        <ScheduleActionItem
          id={actionId}
          key={actionId}
          formikProps={formikProps}
          mode={itemMode}
          onEdit={this.editAction}
          onDelete={id => {
            this.deleteAction(id, index, fieldProps.remove);
          }}
          readOnly={readOnly}
          disabled={disabled}
          apiErrors={violationErrors}
          copyKey={copyKey}
          onChange={this.editAction}
          data={item}
          timezone={timezone}
        />
      );
    });
  };

  render() {
    const { id, readOnly, disabled, copyKey, copy } = this.props;

    return (
      <Segment clearing={true}>
        <Header as="h3">{copy(`${copyKey}.title`)}</Header>
        <FieldArray
          name={id}
          render={fieldProps => {
            const instances = fieldProps.form.values[id];

            return (
              <Fragment>
                {instances && instances.length > 0 && (
                  <Grid columns={3} stackable>
                    {this.renderActionItems(fieldProps)}
                  </Grid>
                )}
                {(readOnly || disabled) &&
                  (!instances || instances.length < 1) && (
                    <p>{copy(`${copyKey}.no_actions`)}</p>
                  )}
                <Divider clearing hidden />
                {!readOnly && !disabled && (
                  <Button
                    onClick={() => this.addAction(fieldProps.push)}
                    icon="plus circle"
                    content={copy(`${copyKey}.add_cta`)}
                    floated="right"
                    type="button"
                  />
                )}
              </Fragment>
            );
          }}
        />
      </Segment>
    );
  }
}

const mapStateToProps = state => ({
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(ScheduleActionsPanel);
